/**
 * 生图适配器注册表（T11a）。
 *
 * 与 T03 的 `src/core/providers/registry.ts` 同构：
 * - 按 `ImageConfig.kind` 实例化适配器；
 * - 支持运行时注册 / 覆盖自定义适配器（`kind: 'custom'` 或覆盖内置 kind）；
 * - 对未注册的 kind 给出明确错误，而不是静默降级。
 *
 * 设计约定：注册表只持有「工厂函数」，不持有配置与密钥，
 * 因此同一个注册表可服务任意多个 ImageConfig 实例。
 */

import { createOpenAIImageAdapter } from './openai';
import {
  ImageError,
  type ImageAdapter,
  type ImageAdapterFactory,
  type ImageConfig,
  type ImageDeps,
  type ImageKind,
  type ResolvedImageConfig,
} from './types';

export const BUILTIN_IMAGE_KINDS: readonly ImageKind[] = ['openai-compatible', 'custom'] as const;

/** 未注册 kind 时抛出；上层可捕获后提示用户去配置。 */
export class ImageNotRegisteredError extends ImageError {
  readonly kind: string;

  constructor(kind: string) {
    super(`没有为 image kind "${kind}" 注册适配器`, 'unsupported');
    this.name = 'ImageNotRegisteredError';
    this.kind = kind;
  }
}

export class ImageRegistry {
  private readonly factories = new Map<string, ImageAdapterFactory>();

  constructor(options?: { withDefaults?: boolean }) {
    if (options?.withDefaults !== false) {
      this.registerDefaults();
    }
  }

  /** 注册内置适配器。T11a 只实现 OpenAI 兼容生图。 */
  registerDefaults(): this {
    this.register('openai-compatible', createOpenAIImageAdapter);
    // 'custom' 默认走 OpenAI 兼容协议（绝大多数自建网关都是 OpenAI 兼容）。
    this.register('custom', createOpenAIImageAdapter);
    return this;
  }

  /** 注册（或覆盖）某个 kind 的适配器工厂。 */
  register(kind: string, factory: ImageAdapterFactory): this {
    if (typeof factory !== 'function') {
      throw new TypeError(`image 工厂必须是函数，收到 ${typeof factory}`);
    }
    this.factories.set(kind, factory);
    return this;
  }

  /** 注销某个 kind。 */
  unregister(kind: string): boolean {
    return this.factories.delete(kind);
  }

  has(kind: string): boolean {
    return this.factories.has(kind);
  }

  /** 已注册的 kind 列表（便于设置页展示与排错）。 */
  kinds(): string[] {
    return [...this.factories.keys()];
  }

  /** 按配置实例化适配器。 */
  create(config: ResolvedImageConfig, deps?: ImageDeps): ImageAdapter {
    const factory = this.factories.get(config.kind);
    if (!factory) {
      throw new ImageNotRegisteredError(config.kind);
    }
    return factory(config, deps);
  }

  /** 批量实例化，跳过 `enabled === false` 的配置。 */
  createEnabled(configs: readonly ResolvedImageConfig[], deps?: ImageDeps): ImageAdapter[] {
    return configs.filter((config) => config.enabled).map((config) => this.create(config, deps));
  }
}

/** 进程内默认注册表（内置 kind 已注册）。 */
export const defaultImageRegistry = new ImageRegistry();

/** 便捷函数：用默认注册表实例化。 */
export function createImageAdapter(config: ResolvedImageConfig, deps?: ImageDeps): ImageAdapter {
  return defaultImageRegistry.create(config, deps);
}

/** 规范化外部配置：补齐必填字段并保证 `enabled` 为布尔值。 */
export function normalizeImageConfig(
  config: ImageConfig & Partial<Pick<ResolvedImageConfig, 'apiKey' | 'headers'>>,
): ResolvedImageConfig {
  return {
    ...config,
    name: config.name || config.kind,
    baseUrl: config.baseUrl,
    apiKeyRef: config.apiKeyRef,
    enabled: config.enabled ?? true,
  };
}